import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PRODUCTS, CONTEXT_IMAGE } from "@/lib/products";
import { REVIEWS, FAQS } from "@/lib/homeContent";
import { LOGO_URL } from "@/lib/brand";
import ParticleCubeHero from "@/components/ParticleCubeHero";
import { TiltCard } from "@/components/TiltCard";
import EmberBackground from "@/components/EmberBackground";
import PhoneDemo from "@/components/PhoneDemo";
import { ArrowUpRight, Mail, MapPin, Sparkles, Star, Plus, Facebook, Instagram, Nfc, QrCode, Palette, Headset, Layers, RefreshCw, BadgeCheck, Check, Rotate3d, Menu, X } from "lucide-react";
import { toast } from "sonner";

const GOLD = "#B2A475";
const GOLD_RGBA = (a) => `rgba(178,164,117,${a})`;
const EASE = [0.22, 1, 0.36, 1];
const FACEBOOK = "https://www.facebook.com/thecubedesign.it/";
const INSTAGRAM = "https://www.instagram.com/thecubedesign.it/";

const Social = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <a href={FACEBOOK} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5" style={{ border: `1px solid ${GOLD_RGBA(0.3)}`, color: "rgba(245,241,230,0.65)" }} onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; e.currentTarget.style.boxShadow = "0 0 20px -6px #B2A475"; }} onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(245,241,230,0.65)"; e.currentTarget.style.boxShadow = "none"; }}>
      <Facebook className="w-4 h-4" />
    </a>
    <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5" style={{ border: `1px solid ${GOLD_RGBA(0.3)}`, color: "rgba(245,241,230,0.65)" }} onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; e.currentTarget.style.boxShadow = "0 0 20px -6px #B2A475"; }} onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(245,241,230,0.65)"; e.currentTarget.style.boxShadow = "none"; }}>
      <Instagram className="w-4 h-4" />
    </a>
  </div>
);

const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const NAV_LINKS = [["#chi-siamo", "Chi siamo"], ["#prodotti", "Prodotti"], ["#come-funziona", "Come funziona"], ["#recensioni", "Recensioni"], ["#faq", "FAQ"], ["#contatti", "Contatti"]];

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ backgroundColor: "rgba(11,11,12,0.72)", borderColor: GOLD_RGBA(0.18) }}>
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="The Cube Design" className="w-9 h-9 object-contain" />
          <span className="text-white font-medium tracking-widest uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem" }}>The Cube Design</span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "rgba(245,241,230,0.6)" }}>
          {NAV_LINKS.map(([href, label]) => (<a key={href} href={href} className="hover:text-white transition-colors">{label}</a>))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline text-sm hover:text-white transition-colors" style={{ color: "rgba(245,241,230,0.7)" }}>Accedi</Link>
          <Link to="/register" data-testid="nav-trial-btn" className="whitespace-nowrap rounded-full text-[13px] sm:text-sm font-semibold px-3.5 sm:px-5 py-2 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_-8px_#B2A475]" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>Prova gratis</Link>
          <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu" className="md:hidden p-2 -mr-2" style={{ color: "#F5F1E6" }}>{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t px-5 py-4 flex flex-col gap-1" style={{ borderColor: GOLD_RGBA(0.18), backgroundColor: "rgba(11,11,12,0.96)" }} data-testid="mobile-menu">
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="py-2.5 text-base" style={{ color: "rgba(245,241,230,0.8)" }}>{label}</a>
          ))}
          <Link to="/configura" onClick={() => setOpen(false)} data-testid="mobile-config-link" className="py-2.5 text-base font-semibold flex items-center gap-2" style={{ color: GOLD }}><Rotate3d className="w-4 h-4" /> Personalizza il tuo cubo</Link>
          <Link to="/login" onClick={() => setOpen(false)} className="py-2.5 text-base sm:hidden" style={{ color: "rgba(245,241,230,0.7)" }}>Accedi</Link>
        </div>
      )}
    </header>
  );
};

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="w-4 h-4" style={{ fill: i < n ? GOLD : "transparent", color: GOLD }} />
    ))}
  </div>
);

const FAQItem = ({ q, a, open, onToggle, index }) => (
  <Reveal delay={index * 0.05}>
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${GOLD_RGBA(0.2)}`, backgroundColor: "rgba(18,18,18,0.75)" }}>
      <button onClick={onToggle} data-testid={`faq-q-${index}`} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]">
        <span className="font-medium text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{q}</span>
        <motion.span animate={{ rotate: open ? 135 : 0 }} transition={{ duration: 0.3 }} className="flex-shrink-0" style={{ color: GOLD }}>
          <Plus className="w-5 h-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} style={{ overflow: "hidden" }}>
            <p className="px-5 pb-5 leading-relaxed" style={{ color: "rgba(245,241,230,0.6)" }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </Reveal>
);

const Landing = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const handleContact = (e) => { e.preventDefault(); toast.success("Grazie! Ti ricontatteremo al più presto."); e.target.reset(); };
  const fieldStyle = { backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${GOLD_RGBA(0.25)}`, color: "#F5F1E6" };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ color: "#F5F1E6", fontFamily: "'Manrope', sans-serif", backgroundColor: "#0B0B0C" }}>
      <Nav />

      {/* Hero — particle cube */}
      <section className="relative min-h-[100vh] flex items-center">
        <ParticleCubeHero className="absolute inset-0 z-0" />
        {/* dark scrim to keep text readable over the animation */}
        <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "radial-gradient(120% 90% at 25% 45%, rgba(11,11,12,0.9) 0%, rgba(11,11,12,0.55) 42%, rgba(11,11,12,0.15) 70%)" }} />
        <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(11,11,12,0.6) 0%, transparent 30%, transparent 65%, #0B0B0C 100%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 w-full pointer-events-none">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.35em]" style={{ color: GOLD }}>The Cube Design</span>
            <h1 className="mt-6 font-medium text-5xl md:text-7xl leading-[1.02]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Il menù del tuo locale,<br /><span className="gold-text">reinventato.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-xl leading-relaxed" style={{ color: "rgba(245,241,230,0.72)" }}>
              Cubi in PVC, legno, plexiglass o nella variante luminosa, totem e insegne collegati a una piattaforma menù digitale editabile in tempo reale.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 pointer-events-auto">
              <a href="#prodotti" data-testid="hero-explore-btn" className="rounded-full font-semibold px-8 py-3.5 border transition-all hover:-translate-y-0.5 hover:bg-white/5" style={{ borderColor: GOLD_RGBA(0.45), color: "#F5F1E6" }}>Scopri i prodotti</a>
              <Link to="/configura" data-testid="hero-config-btn" className="rounded-full font-semibold px-8 py-3.5 border transition-all hover:-translate-y-0.5 hover:bg-white/5 flex items-center gap-2" style={{ borderColor: GOLD_RGBA(0.45), color: GOLD }}><Rotate3d className="w-4 h-4" /> Personalizza il tuo cubo</Link>
              <Link to="/register" data-testid="hero-trial-btn" className="rounded-full font-semibold px-8 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_45px_-6px_#B2A475] flex items-center gap-2" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}><Sparkles className="w-4 h-4" /> Prova gratis</Link>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 pointer-events-auto" style={{ border: `1px solid ${GOLD_RGBA(0.3)}`, color: GOLD_RGBA(0.9), backgroundColor: "rgba(11,11,12,0.4)" }} data-testid="made-in-italy"><BadgeCheck className="w-4 h-4" /> <span className="text-xs uppercase tracking-[0.15em]">Made in Italy</span></div>
          </motion.div>
        </div>
        <div className="absolute bottom-7 inset-x-0 z-10 flex justify-center pointer-events-none">
          <span className="text-[11px] uppercase tracking-[0.3em] animate-pulse" style={{ color: GOLD_RGBA(0.7) }}>Tocca o clicca il cubo</span>
        </div>
      </section>

      {/* Chi siamo / Perché sceglierci */}
      <section id="chi-siamo" className="relative z-10 border-y" style={{ borderColor: GOLD_RGBA(0.12), backgroundColor: "rgba(255,255,255,0.015)" }}>
        <div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>Chi siamo</span>
            <h2 className="mt-3 font-medium text-4xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Perché sceglierci.</h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "rgba(245,241,230,0.7)" }}>Progettiamo e produciamo in Italia oggetti fisici e la piattaforma digitale che li fa vivere: cubi, totem e insegne collegati a un menù sempre aggiornato. Lavoriamo con ristoranti e bar che vogliono un'immagine curata, senza complicazioni tecniche.</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <div className="relative overflow-hidden rounded-3xl" style={{ border: `1px solid ${GOLD_RGBA(0.2)}` }}>
              <img src={CONTEXT_IMAGE} alt="Cube sul tavolo del ristorante" data-testid="chi-siamo-image" className="w-full h-56 md:h-72 object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,11,12,0.85), transparent 65%)" }} />
              <p className="absolute bottom-4 left-5 right-5 text-sm md:text-base" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#F5F1E6" }}>Un unico ecosistema, dal tavolo allo smartphone.</p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{ icon: Layers, t: "Fisico + digitale insieme", d: "Prodotti e piattaforma sviluppati come un'unica soluzione, non due fornitori da far dialogare." }, { icon: RefreshCw, t: "Aggiorni in autonomia", d: "Cambi piatti e prezzi in tempo reale dallo smartphone, quando vuoi." }, { icon: Palette, t: "Design curato", d: "Dai materiali dei Cubi all'interfaccia del menù, ogni dettaglio è pensato." }, { icon: Headset, t: "Assistenza diretta", d: "Persone reali che ti seguono, non un prodotto anonimo comprato online." }].map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="h-full">
                  <div className="h-full rounded-2xl p-6" style={{ backgroundColor: "rgba(18,18,18,0.7)" }}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: GOLD_RGBA(0.12), color: GOLD }}><f.icon className="w-5 h-5" /></div>
                    <h3 className="mt-4 font-semibold text-lg">{f.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(245,241,230,0.55)" }}>{f.d}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Demo interattiva telefono */}
      <section id="demo" className="relative z-10 max-w-6xl mx-auto px-5 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>La demo</span>
            <h2 className="mt-3 font-medium text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Quello che vedranno i tuoi clienti.</h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "rgba(245,241,230,0.65)" }}>Scansionando il Cubetto — o avvicinando il telefono via NFC — i tuoi ospiti aprono un menù come questo. Provalo davvero e confronta il piano <strong style={{ color: GOLD }}>Gratis</strong> con quello <strong style={{ color: GOLD }}>Abbonamento</strong>: foto vere, tema premium e più lingue fanno la differenza.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" className="rounded-full font-semibold px-7 py-3 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_-6px_#B2A475]" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>Prova gratis</Link>
              <a href="#contatti" className="rounded-full font-semibold px-7 py-3 border transition-all hover:-translate-y-0.5 hover:bg-white/5" style={{ borderColor: GOLD_RGBA(0.4), color: "#F5F1E6" }}>Richiedi info</a>
            </div>
          </Reveal>
          <Reveal delay={0.1}><PhoneDemo /></Reveal>
        </div>
      </section>

      {/* Come funziona — due percorsi paralleli */}
      <section id="come-funziona" className="relative z-10 border-y" style={{ borderColor: GOLD_RGBA(0.12), backgroundColor: "rgba(255,255,255,0.015)" }}>
        <div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>Come funziona</span>
            <h2 className="mt-3 font-medium text-4xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Due mondi, come vuoi tu.</h2>
            <p className="mt-4" style={{ color: "rgba(245,241,230,0.6)" }}>Prodotti fisici e piattaforma menù sono offerte indipendenti: scegli l'una, l'altra o entrambe — si abbinano alla perfezione.</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-[1fr_auto_1fr] gap-5 items-stretch">
            <Reveal>
              <TiltCard className="h-full">
                <div className="h-full rounded-3xl p-7" style={{ backgroundColor: "rgba(18,18,18,0.8)" }}>
                  <div className="flex items-center gap-2" style={{ color: GOLD }}><QrCode className="w-5 h-5" /><Nfc className="w-5 h-5" /></div>
                  <h3 className="mt-4 font-medium text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Prodotti fisici</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(245,241,230,0.6)" }}>Cubo, Totem o Piastra esterna, nei materiali che preferisci: PVC, legno, plexiglass o variante luminosa. Ognuno con QR e NFC integrati.</p>
                  <ul className="mt-4 space-y-2 text-sm" style={{ color: "rgba(245,241,230,0.75)" }}>
                    <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> QR + NFC su ogni prodotto</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> Interamente realizzati in Italia</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> Prezzo a listino, inviato su richiesta</li>
                  </ul>
                  <a href="#contatti" data-testid="cf-request-info" className="mt-5 inline-flex rounded-full font-semibold px-6 py-2.5 border transition-all hover:-translate-y-0.5 hover:bg-white/5" style={{ borderColor: GOLD_RGBA(0.4), color: "#F5F1E6" }}>Richiedi info</a>
                </div>
              </TiltCard>
            </Reveal>
            <div className="hidden md:flex items-center justify-center">
              <span className="w-11 h-11 rounded-full flex items-center justify-center text-2xl font-light" style={{ border: `1px solid ${GOLD_RGBA(0.4)}`, color: GOLD, backgroundColor: "#0B0B0C" }}>+</span>
            </div>
            <Reveal delay={0.1}>
              <TiltCard className="h-full">
                <div className="h-full rounded-3xl p-7" style={{ backgroundColor: "rgba(18,18,18,0.8)" }}>
                  <div style={{ color: GOLD }}><Sparkles className="w-5 h-5" /></div>
                  <h3 className="mt-4 font-medium text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Piattaforma menù</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(245,241,230,0.6)" }}>Il menù digitale editabile in tempo reale. Due strade, scegli tu:</p>
                  <div className="mt-4 space-y-3">
                    <div className="rounded-xl p-4" style={{ border: `1px solid ${GOLD_RGBA(0.2)}` }}><p className="font-semibold text-sm">A · Prova gratis</p><p className="text-xs mt-1" style={{ color: "rgba(245,241,230,0.55)" }}>Inizia con la versione gratuita limitata e passa all'abbonamento quando vuoi.</p></div>
                    <div className="rounded-xl p-4" style={{ border: `1px solid ${GOLD_RGBA(0.2)}` }}><p className="font-semibold text-sm">B · Abbonamento diretto</p><p className="text-xs mt-1" style={{ color: "rgba(245,241,230,0.55)" }}>Attiva subito il piano completo, senza passare dalla prova.</p></div>
                  </div>
                  <Link to="/register" className="mt-5 inline-flex rounded-full font-semibold px-6 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_-6px_#B2A475]" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>Inizia ora</Link>
                </div>
              </TiltCard>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Fascia elementi in evidenza */}
      <section className="relative z-10" style={{ backgroundColor: "rgba(178,164,117,0.06)", borderBottom: `1px solid ${GOLD_RGBA(0.12)}` }}>
        <div className="max-w-6xl mx-auto px-5 py-14 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {[{ icon: BadgeCheck, t: "Made in Italy", d: "Prodotti realizzati in Italia" }, { icon: Headset, t: "Assistenza diretta", d: "Supporto reale in italiano" }, { icon: Nfc, t: "QR + NFC", d: "Su ogni prodotto fisico" }].map((h, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex flex-col items-center">
                <h.icon className="w-8 h-8" style={{ color: GOLD }} />
                <p className="mt-3 text-2xl font-medium gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{h.t}</p>
                <p className="mt-1 text-sm" style={{ color: "rgba(245,241,230,0.55)" }}>{h.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Products */}
      <section id="prodotti" className="relative z-10 max-w-6xl mx-auto px-5 py-24 md:py-32">
        <Reveal className="mb-14 text-center">
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "rgba(245,241,230,0.45)" }}>I nostri prodotti</span>
          <h2 className="mt-3 font-medium text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Oggetti che raccontano il tuo locale.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PRODUCTS.map((p, i) => {
            const lone = PRODUCTS.length % 2 === 1 && i === PRODUCTS.length - 1;
            return (
            <Reveal key={p.id} delay={i * 0.08} className={lone ? "md:col-span-2 md:w-[calc(50%-10px)] md:mx-auto" : ""}>
              <TiltCard className="h-full">
                <div className="relative overflow-hidden rounded-3xl h-full flex flex-col" style={{ backgroundColor: "#121212" }}>
                  <div className="relative overflow-hidden h-52">
                    <img src={p.image} alt={p.name} style={{ objectPosition: p.objectPos || "center" }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-active:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #121212 2%, transparent 60%)" }} />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>{p.tagline}</span>
                    <h3 className="mt-2 font-medium text-2xl md:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</h3>
                    <p className="mt-2 leading-relaxed text-sm flex-1" style={{ color: "rgba(245,241,230,0.55)" }}>{p.short}</p>
                    <div className="mt-4 flex items-center gap-5">
                      <Link to={`/prodotti/${p.id}`} data-testid={`product-card-${p.id}`} className="inline-flex items-center gap-1.5 font-semibold w-fit transition-colors hover:text-[#B2A475]" style={{ color: "#F5F1E6" }}>Scopri di più <ArrowUpRight className="w-4 h-4" /></Link>
                      <a href="#contatti" className="text-sm transition-colors hover:text-white" style={{ color: GOLD }}>Richiedi info</a>
                    </div>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
            );
          })}
        </div>
      </section>

      {/* Reviews */}
      <section id="recensioni" className="relative z-10 max-w-6xl mx-auto px-5 pb-24 md:pb-32">
        <Reveal className="mb-12 text-center">
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "rgba(245,241,230,0.45)" }}>Dicono di noi</span>
          <h2 className="mt-3 font-medium text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Locali che ci hanno scelto.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                data-testid={`review-card-${i}`}
                className="h-full rounded-3xl p-7 transition-shadow hover:shadow-[0_24px_60px_-24px_rgba(178,164,117,0.5)]"
                style={{ border: `1px solid ${GOLD_RGBA(0.22)}`, backgroundColor: "rgba(18,18,18,0.8)" }}
              >
                <Stars n={r.rating} />
                <p className="mt-4 leading-relaxed text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(245,241,230,0.85)" }}>“{r.quote}”</p>
                <div className="mt-5 flex items-center gap-2 text-sm">
                  <span className="font-semibold">{r.name}</span>
                  <span style={{ color: "rgba(245,241,230,0.4)" }}>· {r.city}</span>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Platform funnel CTA with rising embers */}
      <section id="piattaforma" className="relative z-10 max-w-6xl mx-auto px-5 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center" style={{ border: `1px solid ${GOLD_RGBA(0.25)}`, backgroundColor: "rgba(18,18,18,0.9)" }}>
            <EmberBackground />
            <div className="relative z-10">
              <span className="text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>La piattaforma menù</span>
              <h2 className="mt-3 font-medium text-4xl md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Attiva il tuo menù digitale in pochi minuti.</h2>
              <p className="mt-4 max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(245,241,230,0.65)" }}>
                Menù sempre aggiornato dal telefono, foto dei piatti, allergeni, traduzioni automatiche e temi eleganti. Niente più menu cartacei o PDF statici. Inizia gratis e passa al piano completo quando vuoi.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/register" data-testid="cta-trial-btn" className="rounded-full font-semibold px-8 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_45px_-6px_#B2A475] flex items-center gap-2" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}><Sparkles className="w-4 h-4" /> Prova gratis</Link>
                <a href="#contatti" data-testid="cta-info-btn" className="rounded-full font-semibold px-8 py-3.5 border transition-all hover:-translate-y-0.5 hover:bg-white/5" style={{ borderColor: GOLD_RGBA(0.4), color: "#F5F1E6" }}>Richiedi info</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-3xl mx-auto px-5 pb-24 md:pb-28">
        <Reveal className="mb-10 text-center">
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "rgba(245,241,230,0.45)" }}>Domande frequenti</span>
          <h2 className="mt-3 font-medium text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tutto quello che vuoi sapere.</h2>
        </Reveal>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FAQItem key={i} index={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contatti" className="relative z-10 max-w-6xl mx-auto px-5 pb-28">
        <Reveal>
          <div className="grid md:grid-cols-2 gap-12 rounded-3xl p-8 md:p-14" style={{ border: `1px solid ${GOLD_RGBA(0.22)}`, backgroundColor: "rgba(14,14,14,0.9)" }}>
            <div>
              <span className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>Contatti</span>
              <h2 className="mt-3 font-medium text-3xl md:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Parliamo del tuo locale.</h2>
              <p className="mt-4 leading-relaxed" style={{ color: "rgba(245,241,230,0.55)" }}>Raccontaci cosa ti serve: ti proponiamo la soluzione Cube su misura per il tuo ristorante o bar.</p>
              <div className="mt-8 space-y-4" style={{ color: "rgba(245,241,230,0.7)" }}>
                <div className="flex items-center gap-3"><Mail className="w-5 h-5" style={{ color: GOLD }} /> info@thecubedesign.it</div>
                <div className="flex items-center gap-3"><MapPin className="w-5 h-5" style={{ color: GOLD }} /> Italia</div>
              </div>
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "rgba(245,241,230,0.45)" }}>Seguici</p>
                <Social />
              </div>
            </div>
            <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
              <input required placeholder="Nome e locale" data-testid="contact-name" className="gold-field w-full rounded-xl px-4 py-3 focus:outline-none" style={fieldStyle} />
              <input required type="email" placeholder="Email" data-testid="contact-email" className="gold-field w-full rounded-xl px-4 py-3 focus:outline-none" style={fieldStyle} />
              <textarea required rows={4} placeholder="Il tuo messaggio" data-testid="contact-message" className="gold-field w-full rounded-xl px-4 py-3 focus:outline-none resize-none" style={fieldStyle} />
              <button type="submit" data-testid="contact-submit" className="w-full rounded-full font-semibold py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_-6px_#B2A475]" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>Invia richiesta</button>
            </form>
          </div>
        </Reveal>
      </section>

      <footer className="relative z-10 border-t py-10" style={{ borderColor: GOLD_RGBA(0.18) }}>
        <div className="max-w-6xl mx-auto px-5 flex flex-col items-center gap-5 text-center">
          <Social />
          <div className="inline-flex items-center gap-2 text-sm" style={{ color: GOLD_RGBA(0.9) }}><BadgeCheck className="w-4 h-4" /> Prodotti realizzati in Italia</div>
          <p className="text-sm" style={{ color: "rgba(245,241,230,0.4)" }}>© {new Date().getFullYear()} The Cube Design · Powered by <a href="https://diamondinnovations.eu" target="_blank" rel="noopener noreferrer" className="hover:text-[#B2A475] transition-colors" style={{ color: "rgba(245,241,230,0.7)" }}>Diamond Innovations</a></p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
